import { ApiQueryName } from "@/models/api"
import type { Organization } from "@/models/organization"
import {
  type PaginatedData,
  paginatedListResponseToPaginatedData,
} from "@/nzds/data-grid"
import type {
  Bill,
  BillListPublicApiQueryOptions,
  BillListResponse,
} from "@/services/core-api/types"
import { useQueryKey, useRenameQueryResult } from "@/services/utils"
import type { HttpError } from "@/utils/api"
import type { UseInfiniteQueryOptions } from "@tanstack/react-query"
import { keepPreviousData, useInfiniteQuery } from "@tanstack/react-query"

import { getBills } from "./get-bills"

interface BillsPaginatedData extends BillListResponse, PaginatedData<Bill> {}

interface UseBillsInfiniteOptions {
  apiQueryOptions?: BillListPublicApiQueryOptions
  organizationId: Organization["id"]
  queryOptions?: Omit<
    UseInfiniteQueryOptions<BillsPaginatedData, HttpError>,
    "queryKey" | "queryFn" | "getNextPageParam" | "initialPageParam" | "select"
  >
}

export const useBillsInfinite = ({
  apiQueryOptions = {},
  organizationId,
  queryOptions = {},
}: UseBillsInfiniteOptions) => {
  const { placeholderData, ...restQueryOptions } = queryOptions

  const queryKey = useQueryKey(ApiQueryName.Bills, "getMany", null, {
    apiQueryOptions,
    organizationId,
  })

  const queryResult = useInfiniteQuery<BillsPaginatedData, HttpError>({
    ...restQueryOptions,
    enabled: Boolean(organizationId) && (restQueryOptions.enabled ?? true),
    placeholderData: placeholderData ?? keepPreviousData,
    queryKey,
    queryFn: async ({ pageParam }) => {
      const offset = Number(pageParam)

      if (!organizationId) {
        throw new Error("organizationId is required to fetch bills")
      }

      const response = await getBills({
        apiQueryOptions: {
          ...apiQueryOptions,
          offset,
        },
        organizationId,
      })

      return paginatedListResponseToPaginatedData(response)
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage) => {
      const nextOffset = lastPage.offset + lastPage.items.length
      if (nextOffset >= lastPage.total) {
        return undefined
      }
      return nextOffset
    },
    throwOnError: restQueryOptions.throwOnError ?? false,
  })

  return useRenameQueryResult(queryResult, ApiQueryName.Bills)
}
